import { GLOBAL_ICONS } from '../../../../util/vars';
import React from 'react';
import type { ToggleModalProps } from '../../../../components/modal/types';
import type { RosterManagementStudentListItem } from '@whizz/api-model';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import type { ExtraDataType } from '../../../../hooks/filters/useStudentActionSelector';
import { useI18n } from '@whizz/react-i18n';
import { CancelButtonModal, SaveButton } from '../../../../global';
import { ModalFooter } from '../../../../components/modal/Modal';

const NotEnoughLicensesModal = ({ toggle, studentsCount, remainingLicensesCount }: NotEnoughLicensesModalProps): JSX.Element => {
  const { translate } = useI18n();

  return (
    <>
      <div className={'container'}>
        <p className={'mb-md'}>
          {/* TODO: Remove ternary after upgrading i18next */}
          {0 < remainingLicensesCount
            ? translate('pages.ROSTER_MANAGEMENT.STUDENTS.NOT_ENOUGH_LICENSES_MODAL.CONTENT.LINE_1', { studentsCount, remainingLicensesCount })
            : translate('pages.ROSTER_MANAGEMENT.STUDENTS.NOT_ENOUGH_LICENSES_MODAL.CONTENT.LINE_1_NO_LICENSES', { studentsCount })
          }
        </p>
        <p className={'mb-md'}>
          {translate('pages.ROSTER_MANAGEMENT.STUDENTS.NOT_ENOUGH_LICENSES_MODAL.CONTENT.LINE_2')}
        </p>
        <p>
          {translate('pages.ROSTER_MANAGEMENT.STUDENTS.NOT_ENOUGH_LICENSES_MODAL.CONTENT.LINE_3')}
        </p>
      </div>
      <ModalFooter>
        <CancelButtonModal
          align={'start'}
          onClick={() => toggle()}
        >
          {translate('pages.ROSTER_MANAGEMENT.STUDENTS.NOT_ENOUGH_LICENSES_MODAL.BUTTONS.CLOSE')}
        </CancelButtonModal>
      </ModalFooter>
    </>
  );
};

const ContinueActionModal = (
  {
    action,
    extraData,
    students,
    onConfirm,
    toggle,
    setShowContinue,
  }: ContinueActionModalProps,
): JSX.Element => {
  const { translate } = useI18n();

  const text = () => {
    switch (action) {
      case RosterManagementStudentActionEnum.moveToClass:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.MOVE_TO_CLASS.CONTENT.LINE_1', { count: students.length, fromClass: extraData.class?.label, toClass: extraData.toClass?.label })}
            </p>
            {extraData.moveYearGroup && (
              <p className={'mb-md'}>
                {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.MOVE_TO_CLASS.CONTENT.MOVE_YEAR_GROUP', { yearGroup: translate(`year_groups.${extraData.toClass?.yearGroupId}`) })}
              </p>
            )}
          </>
        );
      case RosterManagementStudentActionEnum.addToExtraClass:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ADD_TO_EXTRA_CLASS.CONTENT.LINE_1', { count: students.length, toClass: extraData.class?.label })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.removeFromClass:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.REMOVE_FROM_CLASS.CONTENT.LINE_1', { count: students.length, fromClass: extraData.class?.label })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.moveToYearGroup:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.MOVE_TO_YEAR_GROUP.CONTENT.LINE_1', { count: students.length, fromYearGroup: extraData.yearGroup?.label, toYearGroup: extraData.toYearGroup?.label })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.activate:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ACTIVATE.CONTENT.LINE_1', { count: students.length })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.deactivate:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.DEACTIVATE.CONTENT.LINE_1', { count: students.length })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.archive:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ARCHIVE.CONTENT.LINE_1', { count: students.length })}
            </p>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ARCHIVE.CONTENT.LINE_2')}
            </p>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ARCHIVE.CONTENT.LINE_3')}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.unarchive:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.UNARCHIVE.CONTENT.LINE_1', { count: students.length, toClass: extraData.class?.label })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.completelyDeleteArchived:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.COMPLETELY_DELETE_ARCHIVED.CONTENT.LINE_1', { count: students.length })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.addTags:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ADD_TAG.CONTENT.LINE_1', { tag: extraData.tag?.label, count: students.length })}
            </p>
          </>
        );
      case RosterManagementStudentActionEnum.removeTags:
        return (
          <>
            <p className={'mb-md'}>
              {translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.REMOVE_TAG.CONTENT.LINE_1', { tag: extraData.tag?.label, count: students.length })}
            </p>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className={'container'}>
        {text()}
        <div className={'overflow-x-auto overflow-y-hidden p-sm'}>
          <ul className={'list-disc list-inside inline-block min-w-full'} style={{ columnCount: Math.ceil(students.length / 10) }}>
            {students.map((student, index) => (<li key={index} className={'whitespace-nowrap p-1'}>{student.firstName} {student.lastName}</li>))}
          </ul>
        </div>
      </div>
      <ModalFooter>
        <CancelButtonModal
          align={'start'}
          onClick={() => toggle(false)}
        />
        <SaveButton
          align={'start'}
          icon={GLOBAL_ICONS.checkbox}
          showConfirmLabel
          onClick={() => {
            onConfirm(students.map((student) => student.id));
            toggle(false);
            setShowContinue(false);
          }}
        />
      </ModalFooter>
    </>
  );
};

export { NotEnoughLicensesModal, ContinueActionModal };

export interface NotEnoughLicensesModalProps extends ToggleModalProps {
  studentsCount: number;
  remainingLicensesCount: number;
}

export interface ContinueActionModalProps extends ToggleModalProps {
  action: RosterManagementStudentActionEnum,
  extraData: ExtraDataType,
  students: RosterManagementStudentListItem[],
  onConfirm: (studentsIds: number[]) => void,
  setShowContinue: React.Dispatch<React.SetStateAction<boolean>>
}
